import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Cake } from "lucide-react";
import { Typography } from "@akuna/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { StayPack } from "@/hooks/useStayPacks";
import { isStayPackActiveOnDate } from "../reservation/useReservationPacks";
import {
  birthdayPetsOnDate,
  initialsOf,
  parseIsoDate,
  reservationsOnDate,
  weekCells,
  type PlanBirthdayPet,
  type ScheduleReservation,
  type StayShowFilter,
} from "./scheduleModel";

interface WeekGridProps {
  anchor: Date;
  packs: StayPack[];
  reservations: ScheduleReservation[];
  planBirthdayPets: PlanBirthdayPet[];
  selectedDate: string;
  show: StayShowFilter;
  onSelectDate: (isoDate: string) => void;
}

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

/** Nature dot: orange = daycare only, purple = hotel only, blue = both. */
const NATURE_DOT = {
  daycare: "bg-primary",
  hotel: "bg-brand-purple",
  both: "bg-brand-blue",
} as const;

export default function WeekGrid({
  anchor,
  packs,
  reservations,
  planBirthdayPets,
  selectedDate,
  show,
  onSelectDate,
}: WeekGridProps) {
  const { t } = useTranslation("daycareHotel");
  const days = weekCells(anchor);

  return (
    <>
      <div className="-mx-1 mb-[18px] overflow-x-auto px-1 pb-1">
        <div className="grid min-w-[860px] grid-cols-7 gap-2.5">
          {days.map((isoDate, index) => {
            const selected = isoDate === selectedDate;
            const dayReservations = reservationsOnDate(reservations, isoDate, show);
            const daycareCount = dayReservations.filter((reservation) => reservation.nature !== "hotel").length;
            const hotelCount = dayReservations.filter((reservation) => reservation.nature !== "daycare").length;
            const dayPacks = packs.filter((pack) => isStayPackActiveOnDate(pack, isoDate));
            const dayBirthdays = birthdayPetsOnDate(reservations, planBirthdayPets, isoDate, show);

            return (
              <div
                key={isoDate}
                className={`relative flex  flex-col overflow-hidden rounded-xl border-[1.5px] bg-card ${
                  selected ? "border-primary" : "border-border"
                }`}
              >
                <button
                  type="button"
                  onClick={() => onSelectDate(isoDate)}
                  aria-pressed={selected}
                  className="h-full w-full flex items-center justify-start flex-col rounded-2xl border-border px-2.5 pb-[9px] pt-2.5 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <div
                    className={`font-body text-[11px] font-semibold uppercase tracking-[0.05em] ${
                      selected ? "text-orange-600" : "text-neutral-400"
                    }`}
                  >
                    {t(`schedule.weekdayShort.${WEEKDAY_KEYS[index]}`)}
                  </div>
                  <div
                    className={`font-display text-[18px] font-extrabold leading-[1.2] ${
                      selected ? "text-primary" : "text-foreground"
                    }`}
                  >
                    {parseIsoDate(isoDate).getDate()}
                  </div>

                  {daycareCount + hotelCount > 0 && (
                    <div className="mt-1.5 flex flex-col items-center gap-[3px] w-full">
                      <span className="inline-flex w-full items-center gap-1 rounded-md bg-orange-50 px-[7px] py-0.5 font-body text-[10px] font-semibold text-orange-600">
                        {t("schedule.countDaycare", { count: daycareCount })}
                      </span>
                      {hotelCount > 0 && (
                        <span className="inline-flex w-full items-center gap-1 rounded-md bg-purple-50 px-[7px] py-0.5 font-body text-[10px] font-semibold text-purple-700">
                          {t("schedule.countHotel", { count: hotelCount })}
                        </span>
                      )}
                    </div>
                  )}

                  {dayPacks.length > 0 && (
                    <div className="mt-auto flex justify-start gap-[3px] w-full pt-1.5">
                      {dayPacks.map((pack) => (
                        <span
                          key={pack.id}
                          title={pack.name}
                          className="h-[7px] w-[7px] shrink-0 rounded-full bg-brand-purple"
                          style={pack.color ? { background: pack.color } : undefined}
                        />
                      ))}
                    </div>
                  )}
                </button>

                {dayBirthdays.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onClick={(event) => event.stopPropagation()}
                        aria-label={t("schedule.birthdayIconLabel", { count: dayBirthdays.length })}
                        className="absolute right-2 top-2 rounded-full bg-pink-100 p-1 text-pink-600 transition-colors hover:bg-pink-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <Cake aria-hidden="true" size={12} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-56">
                      <Typography variant="caption" className="mb-1.5 block font-semibold text-foreground">
                        {t("schedule.birthdayPopoverTitle")}
                      </Typography>
                      <ul className="flex flex-col gap-1">
                        {dayBirthdays.map((reservation) => (
                          <li key={reservation.petId} className="text-sm">
                            <span className="font-medium">{reservation.petName}</span>
                            <span className="text-muted-foreground"> · {reservation.ownerName}</span>
                          </li>
                        ))}
                      </ul>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
